import { ProfileService } from '../profile.service';
import { System } from './../../../shared-components/System';
import { DomSanitizer } from '@angular/platform-browser';
import { ErrorList } from './../../../shared-components/controls/error-prompt/error-list.model';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import {
	Component,
	Inject,
	OnInit,
	ViewChild,
	ElementRef,
	AfterViewInit,
	HostListener,
	ChangeDetectorRef,
    EventEmitter
} from '@angular/core';
import { Profile } from '../profile.model';
import {AttachmentsMenuDialogComponent} from '../attachments-menu-dialog/attachments-menu-dialog.component';
import {ProfileAttachment} from '../profile-attachment.model';
import { LoginService } from '../../../shared-modules/login/login.service';
import { AddProfileAttachmentDialogComponent } from '../add-profile-attachment-dialog/add-profile-attachment-dialog.component';
import { UserService } from '../../user/user.service';

@Component({
    selector: 'add-edit-content',
    templateUrl: './add-edit-content-dialog.component.html',
    styleUrls: [
        './add-edit-content-dialog.component.css'
    ]
})
export class AddEditContentDialogComponent implements OnInit, AfterViewInit {

    // Properties
    information: Profile;
    originalInformation: Profile = new Profile();
    errors: ErrorList[] = [];
    @ViewChild('titleField', { read: ElementRef }) titleField: ElementRef;
    saved: boolean;
    pictureName = '';
    junkDate = Profile.junkDate;
    @ViewChild('quillContainer', { read: ElementRef }) quillContainer: ElementRef;
    quillHeight = '100px';
    V10 = false;
    userCanEdit = false;
    attachmentChanged = new EventEmitter<any>();

    // Class Functions
    constructor(
        private dialogBox: MatDialogRef<AddEditContentDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private data: any,
        private profileService: ProfileService,
        private sanitizer: DomSanitizer,
        private changeDetector: ChangeDetectorRef,
		private dialog: MatDialog,
        private loginService: LoginService,
        private userService: UserService
    ) { }

    // Event Functions
    ngOnInit() {
        // PRE: dialog data with either:
        //      a) a set of information
        //      b) rowId, topicId, parentId, and sequence
        // POST: initializes the data
        this.V10 = this.loginService.LoginSettings.V10;
        this.userCanEdit = this.data && this.data.userCanEdit != undefined ? this.data.userCanEdit : false;
        if (this.data && this.data.information) {
            this.information = Object.assign(this.data.information);
            Object.assign(this.originalInformation, this.information);
            this.originalInformation.ProfileAttachments = [];
            this.information.ProfileAttachments.forEach(profileAttachment => {
            	const newProfileAttachment = new ProfileAttachment();
            	Object.assign(newProfileAttachment, profileAttachment);
            	this.originalInformation.ProfileAttachments.push(newProfileAttachment);
			});
            if (this.information.Image) {
				this.pictureName = this.information.Title + ' Image';
			}
        } else {
            this.information = new Profile();
            this.information.RowId = this.data.rowId;
            this.information.ProfileContentId = -1;
            this.information.TopicId = this.data.topicId;
            this.information.ParentID = this.data.parentId > 0 ? this.data.parentId : -1;
            this.information.DateFromView = null;
            this.information.DateToView = null;
            this.information.Status = 'A';
            this.information.Title = '';
            this.information.Content = '';
            this.information.Sequence = this.data.sequence;
            this.information.Image = null;
            this.information.DelStatus = false;
            this.information.ProfileAttachments = [];
        }
    }

    @HostListener('window:resize')
    ngAfterViewInit() {
        // PRE: the view has initialized or the window is resized
        // POST: obtains the height of the quill container
        if (this.quillContainer) {
            this.quillHeight = getComputedStyle(this.quillContainer.nativeElement).height;
            this.changeDetector.detectChanges();    // We do this to prevent expression changed errors
        }
    }

    setStatus() {
        // PRE: the active checkbox is clicked
        // POST: sets the status to reflect the active
        if (this.information.Status == 'A') {
            this.information.Status = 'I';
        } else {
            this.information.Status = 'A';
        }
    }

    attachments() {
    	// PRE: the attachments button is clicked
		// POST: opens the attachments dialog
		this.dialog.open(AttachmentsMenuDialogComponent, {
			disableClose: true,
			data: {
				profile: this.information,
				attachments: this.information.ProfileAttachments,
				userCanAdd: true
			}
		});
	}

    save() {
        // PRE: the save button is clicked
        // POST: validates the data, saves the data, and closes the dialog box
        this.validate();
        this.information.cleanJunkDates();
        if (this.errors.length == 0) {
            if (this.information.ProfileContentId == -1) {
                this.profileService.addProfile(this.information).subscribe(newInformation => {
                    this.information = System.convert<Profile>(newInformation, new Profile());
                    this.information.cleanJunkDates();
                    this.saved = true;
                    this.dialogBox.close();
                });
            } else {
                this.profileService.updateProfile(this.information).subscribe(() => {
                    this.saved = true;
                    this.dialogBox.close();
                });
            }
        }
    }

    cancel() {
        // PRE: the cancel button is clicked
        // POST: closes the dialog box
        this.saved = false;
        // Get attachments before initializing the object 
        const attachments  = this.information.ProfileAttachments;
        if (this.data && this.data.information) {
            Object.assign(this.information, this.originalInformation);
        } else {
            this.information = null;
        }
        // Add attachment if any
        if (!this.V10 && attachments && this.information) {
            this.information.ProfileAttachments = attachments;
        }
        this.dialogBox.close();
    }

    // Methods
    validate() {
        // POST: sets the errors
        this.errors = [];
        if (!this.information.Title) {
            this.errors.push({
                fieldName: 'Title',
                fieldRef: this.titleField,
                errorMsg: 'Title is required.'
            });
        }
    }

	resourceSrc() {
		// POST: auto-trusts the data we are given for the image
		return this.sanitizer.bypassSecurityTrustResourceUrl(
			'data: image/jpeg;' +
			'base64,' + this.information.Image
		);
	}

    removePicture() {
        // POST: removes the image from the information
        this.information.Image = null;
        this.pictureName = '';
    }

    addAttachment() {
        // PRE: the add attachment is clicked
        // POST: shows the add attachment dialog
        const attachment = new ProfileAttachment();
        attachment.FileType = '';
        attachment.FileExtension = '';
        attachment.Description = '';
        attachment.DocOwnerID = this.information.ProfileContentId;
        attachment.FolderID = -1;
        attachment.FileID = -1;
        attachment.AllowParentToView = true;
        attachment.TableID = 18;
        attachment.MyFileName = '';
        attachment.OriginalFileName = '';
        attachment.DateAttached = new Date();
        attachment.ContentID = this.information.ProfileContentId
        attachment.UploadedBy = this.userService.lastname + ', ' + this.userService.firstname;
        const dialogBox = this.dialog.open(AddProfileAttachmentDialogComponent, {
          data: {
            attachment: attachment
          },
          disableClose: true
        });
        dialogBox.afterClosed().subscribe(() => {
            if (dialogBox.componentInstance.saved) {
                this.information.ProfileAttachments.push(dialogBox.componentInstance.attachment)
                this.attachmentChanged.emit();
            }
          })
      }
}
